import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import makeStyles from "utils/makeStyles";
import withStyles from "utils/withStyles";
import moment from "dayjs";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Grid from "utils/Grid";
import Typography from "@mui/material/Typography";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from "@mui/material/TextField";
import { ApplyButton } from "components/Common/Buttons";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { CloudDownload } from "@mui/icons-material";
import { useLocation, useParams } from "react-router";
import _ from "lodash";
import CustomInputDatePicker from "../../../../../Common/CustomInputDatePicker";
import SearchBox from "../common/SearchBox";
import ContentHeader from "../common/ContentHeader";
import TableGenerator from "../common/CollapsibleTableGenerator";
import { handleMultiFilterSearch } from "helpers";
import {
  convertUtcToLocal,
  dateFormatter,
  getFilterEndDate,
  getFilterStartDate,
  getTimeZoneDifference,
  currencyFormatter,
} from "utils/helpers";
import styles from "./styles";
import "./styles.css";

const filterLabels = {
  order_id: "order_id",
  amount: "amount",
};

const deviceTypeFields = ["order_id", "amount"];

const Orders = () => {
  const classes = styles();
  const { deviceId } = useParams();
  const location = useLocation();
  const [ordersList, setOrdersList] = useState([]);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [ordering, setOrdering] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [query, setQuery] = useState("");
  const [loader, setLoader] = useState(false);
  const current_user = useSelector((state) => state.userReducer.current_user);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "weeks").toDate()
  );
  const [exportLoader, setExportLoader] = useState(false);
  const [filterable, setFilterable] = useState(deviceTypeFields[0]);
  const [endDate, setEndDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [enableTaxes, setEnableTaxes] = useState(false);
  const enableTaxesPerVM = useSelector(
    (state) => state.companySettingsReducer.enableTaxesPerVM
  );
  const productsData = useSelector(
    (state) => state.dropdownReducer.productsData
  );
  const orderStatusData = useSelector(
    (state) => state.dropdownReducer.orderStatusData
  );

  const [page, setPage] = useState(0);

  const navigate = useNavigate();

  function BootstrapTooltip(props) {
    const classes = useStylesBootstrap();

    return (
      <Tooltip arrow placement={props.place} classes={classes} {...props} />
    );
  }

  const useStylesBootstrap = makeStyles((theme) => ({
    arrow: {
      color: theme.palette.common.black,
    },
    tooltip: {
      backgroundColor: theme.palette.common.black,
      fontSize: 13,
    },
  }));

  const downloadPDF = (order_id) => {
    window.open(`/receipts/${order_id}`, "_blank");
  };

  const collapsibleTableFields = [
    {
      key: "product_id",
      columnName: "Product Id",
    },
    {
      key: "product_sku",
      columnName: "Product SKU",
      render: (val, value) =>
        val?.product_sku
          ? val?.product_sku
          : _.find(productsData, (x) => x.id == val.product_id)?.product_sku ||
            "---",
    },
    {
      key: "product_name",
      columnName: "Product Name",
    },
    {
      key: "module_number",
      columnName: "Cabinet",
      render: (values) => {
        return values?.module_number
          ? `Module ${values.module_number?.split("-")[1]}`
          : "1";
      },
    },
    {
      key: "rack_no",
      columnName: "Rack",
      render: (values) => {
        if (values.row === -1 || values.col === -1) {
          return "---";
        }
        return `${values.row}*${values.col}`;
      },
    },
    {
      key: "quantity",
      columnName: "Quantity",
    },
    {
      key: "product_price",
      columnName: "Price ($)",
      render: (value) => currencyFormatter(value.product_price) ?? "---",
    },
    {
      key: "product_status",
      columnName: "Dispense Status",
    },
    {
      key: "total_amount",
      columnName: "Total Amount ($)",
      render: (value) => currencyFormatter(value.total_amount) ?? "---",
    },
    {
      key: "deals",
      columnName: "Discount Codes",
      render: (value) =>
        value?.deals.map((x) => x.discount_code)?.join(", ") || "---",
    },
    {
      key: "metrc_tag_id",
      columnName: "Metrc Tag",
      render: (value) => value.metrc_tag_id ?? "---",
    },
  ];

  const collapsibleTableFields2 = [
    {
      key: "status",
      columnName: "Order Status",
      render: (value) => value.status ?? "---",
    },
    {
      key: "order_date",
      columnName: "Date and Time",
      render: (value) =>
        dateFormatter(convertUtcToLocal(value.order_date), true) ?? "---",
    },
  ];

  const collapsibleTableFields3 = [
    {
      key: "product_cost",
      columnName: "MRP ($)",
      visible: true,
      render: (value) =>
        value?.product_cost || value?.product_cost == 0
          ? currencyFormatter(value.product_cost)
          : "---",
    },
    {
      key: "total_discount",
      columnName: "Discount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.total_discount) ?? "---",
    },
    {
      key: "order_amount",
      columnName: "Order Amount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.order_amount) ?? "---",
    },
    {
      key: "tax",
      columnName: "Additional Tax ($)",
      visible: enableTaxes,
      render: (value) => currencyFormatter(value.tax) ?? "---",
    },
    {
      key: "tax_per_vm",
      columnName: "Tax ($)",
      visible: true,
      render: (value) => currencyFormatter(value.tax_per_vm) ?? "---",
    },
    {
      key: "total_amount",
      columnName: "Total Amount ($)",
      visible: true,
      render: (value) => currencyFormatter(value.total_amount) ?? "---",
    },
    {
      key: "error_message",
      columnName: "Error Message",
      visible: true,
      render: (value) => value.error_message ?? "---",
    },
  ];

  const fields = [
    {
      key: "order_id",
      columnName: "ID",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_status",
      columnName: "Order status",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_amount",
      columnName: "Amount ($)",
      type: "text",
      visible: true,
      render: (value) => currencyFormatter(value) ?? "---",
    },
    {
      key: "total_order",
      columnName: "No of Items Ordered",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_success",
      columnName: "No of Items Delivered",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "total_failed",
      columnName: "No of Items Failed",
      type: "text",
      visible: true,
      disableSorting: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_data",
      columnName: "Order Items",
      type: "text",
      disableSorting: true,
      render: (val) => {
        if (!val || val.length === 0) return "---";
        const firstTwo = val.slice(0, 2).join(", ");
        const remaining = val.length - 2;
        return remaining > 0 ? `${firstTwo} +${remaining} more` : firstTwo;
      },
      visible: true,
    },
    {
      key: "user",
      columnName: "User",
      type: "text",
      visible: true,
      render: (val) => val ?? "---",
    },
    {
      key: "order_date",
      columnName: "Placed at",
      type: "text",
      visible: true,
      render: (value) => dateFormatter(convertUtcToLocal(value), true) ?? "---",
    },
    {
      key: "data",
      columnName: "E-Receipt",
      type: "text",
      form: false,
      required: false,
      visible: true,
      render: (_, value) => (
        <>
          <BootstrapTooltip title={"Download E-receipt"}>
            <IconButton
              size="small"
              className="m-1"
              variant="contained"
              disabled={
                !["ITEM_DISPENSING_FAILED_PARTIAL", "SUCCESSFUL"].includes(
                  value.order_status
                )
              }
              onClick={() => {
                downloadPDF(value.order_id);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </BootstrapTooltip>

          <BootstrapTooltip>
            <IconButton
              size="small"
              className="m-1"
              variant="contained"
              disabled={
                !["ITEM_DISPENSING_FAILED_PARTIAL", "SUCCESSFUL"].includes(
                  value.order_status
                )
              }
              style={{ display: "none" }}
              onClick={() => {
                downloadPDF(value.order_id);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </BootstrapTooltip>
        </>
      ),
    },
  ];

  const handleFilter = async (arr) => {
    setFilterable(arr);
    if (query !== "") {
      setup();
      setPage(0);

      let { order_id, amount } = handleMultiFilterSearch(
        filterLabels,
        [arr],
        query
      );

      try {
        let params = {
          order_id,
          amount,
          start_date: getFilterStartDate(startDate),
          end_date: getFilterEndDate(endDate),
          device_id: deviceId,
          limit: rowsPerPage,
          history: true,
          _scope: "AND",
        };
        if (statusFilter && statusFilter == "all") {
          params["latest"] = true;
        }
        if (statusFilter && statusFilter !== "all") {
          params["status"] = statusFilter;
        }
        const { data } = await window.axiosIns("orders/custom-orders", {
          params,
        });
        handleRes(data);
      } catch (err) {
        console.log(err);
      }
    }
  };

  const setup = () => {
    setLoader(true);
    setOrdersList([]);
    setModulesSelected([]);
  };

  const handleRes = (data = []) => {
    setOrdersList(data?.results);
    setLoader(false);
    setNextPage(data.next);
    setPreviousPage(data.previous);
  };

  const getOrders = async (order, max, pageNumber = null) => {
    try {
      setup();

      const realPage = pageNumber ? 1 : page + 1;

      let { order_id, amount } = handleMultiFilterSearch(
        filterLabels,
        [filterable],
        query
      );

      const params = {
        order_id,
        amount,
        limit: max ? max : rowsPerPage,
        ordering: order ? order : ordering,
        page: realPage,
        start_date: getFilterStartDate(startDate),
        end_date: getFilterEndDate(endDate),
        device_id: deviceId,
        history: true,
        _scope: "AND",
      };

      if (statusFilter && statusFilter == "all") {
        params["latest"] = true;
      }

      if (statusFilter && statusFilter !== "all") {
        params["status"] = statusFilter;
      }

      const { data } = await window.axiosIns("orders/custom-orders", {
        params,
      });
      handleRes(data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getOrders(null, null, true);
  }, []);

  useEffect(() => {
    setEnableTaxes(enableTaxesPerVM);
  }, [enableTaxesPerVM]);

  const changePage = (url) => {
    setup();
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        handleRes(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleExport = async () => {
    let { order_id, amount } = handleMultiFilterSearch(
      filterLabels,
      [filterable],
      query
    );
    setExportLoader(true);
    let params = {
      order_id,
      amount,
      device_id: deviceId,
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference(),
      _scope: "AND",
    };
    if (statusFilter && statusFilter !== "all") {
      params["status"] = statusFilter;
    }
    window
      .axiosIns("/orders/export_data", {
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/json",
        },
        params,
      })
      .then((response) => {
        setExportLoader(false);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Orders-Summary-${moment().format("MM-DD-YYYY")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        setExportLoader(false);
        console.log(error);
      });
  };

  const handleSearch = async (value) => {
    setQuery(value);
    setup();
    setPage(0);

    let { order_id, amount } = handleMultiFilterSearch(
      filterLabels,
      [filterable],
      value
    );
    const params = {
      order_id,
      amount,
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      device_id: deviceId,
      limit: rowsPerPage,
      history: true,
      _scope: "AND",
    };
    if (statusFilter && statusFilter == "all") {
      params["latest"] = true;
    }

    if (statusFilter && statusFilter !== "all") {
      params["status"] = statusFilter;
    }

    try {
      const { data } = await window.axiosIns("orders/custom-orders", {
        params,
      });
      handleRes(data);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader
        // title="Orders"
        description="All orders automatically show up here. You can filter the statements as well."
      />

      <Card
        style={{ margin: "0px 20px 10px 20px" }}
        variant="contained"
        className={classes.headerToolbar}
      >
        <Grid style={{ padding: "10px 20px" }} container>
          <Grid xs={12} sm={4}>
            <Typography variant="subtitle2">
              <strong>VM Serial : </strong>
              {location?.state?.serial_number || ""}
            </Typography>
          </Grid>
          <Grid xs={12} sm={4}>
            <Typography variant="subtitle2">
              <strong>VM Name : </strong>
              {location?.state?.vm_name || ""}
            </Typography>
          </Grid>
        </Grid>
      </Card>

      <div className={classes.toolbar}>
        <Grid
          spacing={1}
          style={{ alignItems: "center" }}
          container
          className="align-center"
        >
          <Grid md={2} xs={2}>
            <CustomInputDatePicker
              inputClassName=""
              value={startDate}
              maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
              onChange={(date) => setStartDate(date)}
              label="Start Date"
            />
          </Grid>

          <Grid md={2} xs={2}>
            <CustomInputDatePicker
              inputClassName=""
              value={endDate}
              minDate={new Date(startDate)}
              maxDate={new Date()}
              onChange={(date) => setEndDate(date)}
              label="End Date"
            />
          </Grid>

          <Grid md={2} xs={2}>
            <Autocomplete
              style={{ width: "100%" }}
              id="tags-outlined"
              value={
                orderStatusData.find((opt) => opt.value === statusFilter) ||
                null
              }
              loading={false}
              options={Array.isArray(orderStatusData) ? orderStatusData : []}
              defaultValue={[{ label: "All", value: "all" }]}
              onChange={(_, newValue) => {
                setStatusFilter(newValue?.value || "");
              }}
              getOptionLabel={(option) => option?.label || ""}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Order Status"
                  slotProps={{ ...params.slotProps, input: { ...params.slotProps.input,
                    endAdornment: <>{params.slotProps.input.endAdornment}</>,
                  } }}
                />
              )}
            />
          </Grid>
          <Grid md={1} xs={1}>
            <ApplyButton
              style={{ height: 56, width: "100%" }}
              onClick={() => getOrders(null, null, true)}
            />
          </Grid>
          <Grid md={2.5} xs={2.5}>
            <SearchBox
              style={{ height: 56 }}
              iconStyle={{ height: 56 }}
              multiple={false}
              query={query}
              onChange={handleFilter}
              fields={deviceTypeFields}
              selectedFields={filterable}
              handleSearch={handleSearch}
            />
          </Grid>
          <Grid md={1.5} xs={1.5}>
            {current_user.type !== "SU" && (
              <Button
                style={{ height: 56, width: "100%", padding: "6px 16px" }}
                size="small"
                color="primary"
                variant="contained"
                disabled={exportLoader || !ordersList.length}
                onClick={handleExport}
              >
                {exportLoader ? (
                  <>
                    <CircularProgress
                      color="#fff"
                      size={16}
                      style={{ marginRight: 0 }}
                    />
                  </>
                ) : (
                  <>
                    <CloudDownload className="mr-1" fontSize="small" />{" "}
                  </>
                )}
                Export
              </Button>
            )}
          </Grid>
          <Grid md={1} xs={1}>
            <Button
              style={{ height: 56, width: "100%" }}
              size="small"
              color="primary"
              variant="contained"
              onClick={() =>
                navigate("/finance?tab=orders_and_transactions")
              }
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </div>
      <div className={classes.content}>
        <TableGenerator
          searchQuery={query}
          minimal={true}
          nextPage={nextPage}
          previousPage={previousPage}
          initialSort={"id"}
          searchColumnsFilter={true}
          fields={fields}
          loader={loader}
          data={ordersList}
          currentPage={page}
          rowOptions={[3, 5, 7, 8, 10, 25, 50, 100]}
          handleSortChange={(ordering) => {
            setOrdering(ordering);
            getOrders(ordering);
          }}
          onPageChange={(page, direction) => {
            setPage(page);
            if (direction === "next") {
              changePage(nextPage);
            } else if (direction === "back") {
              changePage(previousPage);
            }
          }}
          backendPagination={true}
          onRowPerPageChange={(rows) => {
            getOrders(null, rows, true);
            setRowsPerPage(rows);
            setPage(0);
          }}
          onChangePage={(page) => console.log(page)}
          selectedRecords={modulesSelected}
          collapsible={true}
          collapsibleHeader={"Order Details"}
          collapsibleFields={collapsibleTableFields}
          collapsibleFieldKey={"order_items"}
          collapsibleHeader2={"Order History"}
          collapsibleFields2={collapsibleTableFields2}
          collapsibleFieldKey2={"history"}
          collapsibleFieldKey3={"order_summery"}
          collapsibleHeader3={"Order Summary"}
          collapsibleFields3={collapsibleTableFields3}
          rowOnePage={25}
          onChangeSelected={(modulesSelected) =>
            setModulesSelected(modulesSelected)
          }
        />
      </div>
    </div>
  );
};
export default withStyles({}, { withTheme: true })(Orders);
